import { useEffect, useRef, useState } from 'react';
import { Download, ArrowDown } from 'lucide-react';

const FORMSPREE_URL = 'https://formspree.io/f/mvzbwoak';
const PDF_PATH = '/Files/The-Silent-Cipher-Free-Chapters.pdf';

export const FreePreviewSection = () => {
  const [visible, setVisible] = useState(false);
  const [formData, setFormData] = useState({ name: '', email: '' });
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [error, setError] = useState('');
  const sectionRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) setVisible(true);
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  const handleChange = (e) => {
    setFormData((prev) => ({ ...prev, [e.target.name]: e.target.value }));
    setError('');
  };

  const triggerDownload = () => {
    const link = document.createElement('a');
    link.href = PDF_PATH;
    link.download = 'The-Silent-Cipher-Free-Chapters.pdf';
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name.trim() || !formData.email.trim()) {
      setError('Please fill in all fields.');
      return;
    }
    setSubmitting(true);
    setError('');

    try {
      const res = await fetch(FORMSPREE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ name: formData.name, email: formData.email }),
      });
      if (res.ok) {
        setSubmitted(true);
        triggerDownload();
        setFormData({ name: '', email: '' });
      } else {
        setError('Something went wrong. Please try again.');
      }
    } catch {
      setError('Network error. Please try again.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <section
      id="free-preview"
      ref={sectionRef}
      className="relative py-20 md:py-32 overflow-hidden"
      style={{ background: '#0d0f12' }}
    >
      {/* Top separator */}
      <div className="absolute top-0 left-5 right-5 md:left-10 md:right-10 h-px bg-gradient-to-r from-transparent via-[rgba(0,212,255,0.15)] to-transparent" />

      {/* Subtle background accent */}
      <div
        className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] opacity-[0.03] pointer-events-none"
        style={{ background: 'radial-gradient(circle, #00d4ff, transparent 65%)' }}
      />

      <div className="max-w-[87.5rem] mx-auto px-5 md:px-10">
        <div className="max-w-2xl mx-auto text-center">
          {/* Hook line */}
          <p
            className={`font-cardo text-sm md:text-base text-[#666] italic mb-4 transition-all duration-1000 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            Before you buy the book, enter the world.
          </p>

          {/* Main headline */}
          <h2
            className={`font-montserrat font-800 text-2xl md:text-4xl lg:text-5xl uppercase text-[#e8e6e1] leading-[0.9] mb-5 transition-all duration-1000 delay-100 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            Download the First 2 Chapters{' '}
            <span className="text-[#00d4ff]">— Free</span>
          </h2>

          {/* Subtext */}
          <p
            className={`font-cardo text-base md:text-lg text-[#888680] leading-relaxed mb-10 md:mb-14 max-w-lg mx-auto transition-all duration-1000 delay-200 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            Step into the world of The Silent Cipher. Get the Prologue and Chapter One — no cost.
          </p>

          {/* Form or Success */}
          <div
            className={`transition-all duration-1000 delay-300 ${
              visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-6'
            }`}
          >
            {submitted ? (
              /* Success state */
              <div className="flex flex-col items-center gap-4 py-6">
                <div className="w-14 h-14 rounded-full bg-[rgba(0,212,255,0.1)] flex items-center justify-center">
                  <Download size={24} className="text-[#00d4ff]" />
                </div>
                <p className="font-montserrat font-600 text-lg text-[#e8e6e1]">
                  Your download has started!
                </p>
                <p className="font-cardo text-sm text-[#888680]">
                  If it didn't,{' '}
                  <button
                    onClick={triggerDownload}
                    className="text-[#00d4ff] underline underline-offset-2 cursor-pointer bg-transparent border-none font-cardo text-sm"
                  >
                    click here to download
                  </button>
                  .
                </p>
              </div>
            ) : (
              /* Form */
              <form
                onSubmit={handleSubmit}
                className="max-w-md mx-auto"
              >
                <div className="flex flex-col gap-4">
                  {/* Name */}
                  <input
                    type="text"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    required
                    placeholder="Your name"
                    className="w-full bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] rounded-xl px-5 py-3.5 font-cardo text-base text-[#e8e6e1] placeholder-[#444] outline-none transition-all duration-300 focus:border-[rgba(0,212,255,0.3)] focus:bg-[rgba(0,212,255,0.02)] text-center"
                  />

                  {/* Email */}
                  <input
                    type="email"
                    name="email"
                    value={formData.email}
                    onChange={handleChange}
                    required
                    placeholder="Your email address"
                    className="w-full bg-[rgba(255,255,255,0.04)] border border-[rgba(255,255,255,0.08)] rounded-xl px-5 py-3.5 font-cardo text-base text-[#e8e6e1] placeholder-[#444] outline-none transition-all duration-300 focus:border-[rgba(0,212,255,0.3)] focus:bg-[rgba(0,212,255,0.02)] text-center"
                  />

                  {/* Error */}
                  {error && (
                    <p className="font-montserrat text-xs text-red-400">{error}</p>
                  )}

                  {/* Submit */}
                  <button
                    type="submit"
                    disabled={submitting}
                    className="inline-flex items-center justify-center gap-3 w-full font-montserrat font-semibold text-sm tracking-wider uppercase px-8 py-4 rounded-full bg-[#00d4ff] text-[#0d0f12] border-none cursor-pointer transition-all duration-300 hover:scale-[1.02] hover:shadow-[0_0_40px_rgba(0,212,255,0.2)] active:scale-[0.98] disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100 mt-1"
                  >
                    {submitting ? (
                      <span className="inline-block w-5 h-5 border-2 border-[#0d0f12]/30 border-t-[#0d0f12] rounded-full animate-spin" />
                    ) : (
                      <ArrowDown size={16} />
                    )}
                    {submitting ? 'Submitting...' : 'Download Free Chapters'}
                  </button>
                </div>
              </form>
            )}
          </div>
        </div>
      </div>
    </section>
  );
};
